/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useState} from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {Link as RouteLink} from 'react-router-dom'
// Components
import {
    Box,
    Text,
    Image,
    Skeleton,
    useStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {getUrl} from '../../../utils/cloudinary-utils'
import {Link as ChakraLink} from '@salesforce/retail-react-app/app/components/shared/ui'
import {searchUrlBuilder} from '../../../utils/url'
import {useSiteContext} from '../../../contexts/site'
import {yieldToMain} from '../../../utils/common-utils'
import LoadingSpinner from '../../../components/loading-spinner'

const AutoCorrect = ({
    brAutoCorrectQuery = '',
    searchQuery,
    isLoading,
    brDidYouMean = [],
    enableBloomreachSearch = false
}) => {
    const intl = useIntl()
    const {isYR} = useSiteContext()
    const styles = useStyleConfig('ProductListStyles')
    const [actionSpinner, showActionSpinner] = useState(false)
    const displayAutoCorrect = brAutoCorrectQuery && searchQuery && enableBloomreachSearch
    const showDidYouMean = !!brDidYouMean?.length && searchQuery
    return !isLoading ? (
        <Box display={displayAutoCorrect || showDidYouMean ? 'inline-grid' : 'none'}>
            <Box
                marginTop="15px"
                display={{
                    base: displayAutoCorrect ? 'grid' : 'none',
                    md: displayAutoCorrect ? 'inline-flex' : 'none'
                }}
                gap={{base: '10px', md: '30px'}}
            >
                <Text {...styles.noSrp.queryCorrection.text2}>
                    {intl.formatMessage(
                        {
                            id: 'search.auto_corrected_from',
                            defaultMessage: 'Auto-corrected from “{searchQuery}”'
                        },
                        {
                            searchQuery: searchQuery
                        }
                    )}
                </Text>

                <Box
                    {...styles.noSrp.queryCorrection.main}
                    onClick={() => {
                        if (window.screen.width < 992) {
                            document.querySelector('.open-search-mobile').click()
                        } else {
                            document.querySelector('.open-search').click()
                        }
                    }}
                    display="inline-flex"
                    className="try-new-search"
                >
                    <Image
                        src={getUrl('UIImages/images/blue-search-icon.svg')}
                        width="14px"
                        height="14px"
                        alt="Search Icon"
                    />
                    <Text {...styles.noSrp.queryCorrection.text1}>
                        {intl.formatMessage({
                            id: 'search.try_a_new',
                            defaultMessage: 'Try a New Search'
                        })}
                    </Text>
                </Box>
            </Box>

            <Box
                marginTop="15px"
                gap={{base: '0px', md: '30px'}}
                {...styles.noSrp.queryCorrection.main}
                display={{
                    base: showDidYouMean
                        ? brDidYouMean?.[0]?.length > 45
                            ? 'grid'
                            : 'inline-flex'
                        : 'none',
                    md: showDidYouMean ? 'inline-flex' : 'none'
                }}
            >
                <Text {...styles.noSrp.queryCorrection.text2}>
                    {intl.formatMessage({
                        id: 'search.did_you_mean',
                        defaultMessage: 'Did you mean'
                    })}
                </Text>
                <Box display="inline-flex">
                    <Image
                        src={getUrl('UIImages/images/blue-search-icon.svg')}
                        width="14px"
                        height="14px"
                        alt="Search Icon"
                        margin="6px 10px 0px 0"
                    />

                    <ChakraLink
                        as={RouteLink}
                        onClickCapture={async () => {
                            showActionSpinner(true)
                            await yieldToMain()
                            setTimeout(() => {
                                showActionSpinner(false)
                            }, 100)
                        }}
                        isExternal={false}
                        display={brDidYouMean?.[0]?.length > 45 ? 'contents' : 'inline-flex'}
                        to={searchUrlBuilder(isYR, brDidYouMean?.[0])}
                        textDecoration="none"
                        _hover={{
                            textDecoration: 'none'
                        }}
                    >
                        <Text
                            {...styles.noSrp.queryCorrection.text1}
                            as="span"
                            textDecoration="underline"
                            textUnderlineOffset="4px"
                        >
                            {brDidYouMean?.[0]} ?
                        </Text>
                    </ChakraLink>
                </Box>
            </Box>
            {actionSpinner && <LoadingSpinner position="fixed" top="auto" />}
        </Box>
    ) : (
        <Box display={searchQuery ? 'inline-grid' : 'none'}>
            <Box
                marginTop="15px"
                display={{
                    base: 'block',
                    md: 'inline-flex'
                }}
                gap={{base: '0px', md: '30px'}}
            >
                <Skeleton height="22px" width="200px" />
                <Skeleton marginTop={{base: '10px', md: '0px'}} height="22px" width="170px" />
            </Box>

            <Box
                marginTop="15px"
                gap={{base: '0px', md: '30px'}}
                {...styles.noSrp.queryCorrection.main}
                display="inline-flex"
            >
                <Skeleton height="22px" width="120px" />
                <Skeleton height="22px" width="80px" />
            </Box>
        </Box>
    )
}

AutoCorrect.propTypes = {
    brAutoCorrectQuery: PropTypes.any,
    searchQuery: PropTypes.any,
    isLoading: PropTypes.bool,
    brDidYouMean: PropTypes.array,
    enableBloomreachSearch: PropTypes.bool
}

export default AutoCorrect
